import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Calendar, Phone, MapPin, Clock, User, Search, ChevronDown } from 'lucide-react';

export const Templates: React.FC = () => {
  const templates = [
    {
      title: "Family Physician Office",
      category: "Healthcare",
      image: "/templates/family-physician-preview.jpg",
      description: "A professional template designed for family medicine practices with appointment booking and patient resources.",
      features: ["Online Appointments", "Patient Portal", "Staff Profiles", "Services Section", "Health Resources"],
      demoLink: "/templates/family-physician",
      colors: ["#2563eb", "#10b981", "#ffffff"],
      popular: true
    },
    {
      title: "Medical Spa",
      category: "Healthcare",
      image: "/templates/medical-spa-preview.jpg",
      description: "A luxury template for medical spas and aesthetic clinics with membership features and treatment showcases.",
      features: ["Treatment Gallery", "Membership Plans", "Online Booking", "Staff Profiles", "Before/After Gallery"],
      demoLink: "/templates/medical-spa",
      colors: ["#9333ea", "#ec4899", "#ffffff"],
      popular: true
    },
    {
      title: "Business Pro",
      category: "Corporate",
      image: "/templates/business-pro-preview.jpg",
      description: "A professional template designed for established businesses looking for a polished online presence.",
      features: ["Responsive Design", "Contact Forms", "Team Showcase", "Services Section", "Testimonials"],
      demoLink: "/templates/business-pro",
      colors: ["#1e40af", "#6b21a8", "#f3f4f6"],
      popular: false
    },
    {
      title: "E-Commerce Essential",
      category: "Online Store",
      image: "/templates/ecommerce-preview.jpg",
      description: "Everything you need to start selling online with a clean, conversion-focused design.",
      features: ["Product Catalog", "Shopping Cart", "Payment Processing", "Inventory Management", "Mobile Optimized"],
      demoLink: "/templates/ecommerce-essential",
      colors: ["#059669", "#7c3aed", "#ffffff"],
      popular: false
    },
    {
      title: "Restaurant Deluxe",
      category: "Food & Beverage",
      image: "/templates/restaurant-preview.jpg",
      description: "Showcase your menu and ambiance with this template designed specifically for restaurants.",
      features: ["Menu Display", "Online Reservations", "Photo Gallery", "Location Map", "Mobile Ordering"],
      demoLink: "/templates/restaurant-deluxe",
      colors: ["#b91c1c", "#92400e", "#f8fafc"],
      popular: false
    },
    {
      title: "Creative Portfolio",
      category: "Portfolio",
      image: "/templates/portfolio-preview.jpg",
      description: "Showcase your creative work with this visually stunning portfolio template.",
      features: ["Project Gallery", "About Section", "Contact Form", "Blog Integration", "Social Media Links"],
      demoLink: "/templates/creative-portfolio",
      colors: ["#0f172a", "#f97316", "#f1f5f9"],
      popular: false
    },
    {
      title: "Real Estate Pro",
      category: "Real Estate",
      image: "/templates/real-estate-preview.jpg",
      description: "Designed for real estate agents and property managers to showcase listings effectively.",
      features: ["Property Listings", "Search Functionality", "Agent Profiles", "Inquiry Forms", "Map Integration"],
      demoLink: "/templates/real-estate-pro",
      colors: ["#0c4a6e", "#166534", "#ffffff"],
      popular: true
    }
  ];

  const faqs = [
    {
      question: "How do I purchase a template?",
      answer: "Simply click the 'View Demo' button on any template that interests you to see the full demo. Then click 'Get Started' to begin the purchase process, including customization options."
    },
    {
      question: "Can I customize the template to match my brand?",
      answer: "Yes! All templates can be customized with your brand colors, logo, fonts, and content. We can handle the customization for you or provide guidance if you prefer to do it yourself."
    },
    {
      question: "Do I need to purchase hosting separately?",
      answer: "No, all our template packages include hosting for the first year. After that, hosting can be renewed at our standard rates or transferred to your own hosting provider."
    },
    {
      question: "How long does it take to set up a template?",
      answer: "Most templates can be set up within 3-5 business days after we receive your content and customization preferences."
    },
    {
      question: "Can I upgrade to a custom website later?",
      answer: "Absolutely! If your business outgrows the template, we offer upgrade paths to fully custom websites with a discount for existing template customers."
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="pt-32 pb-32 bg-gradient-to-br from-primary-600 to-secondary-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
              Website Templates
            </h1>
            <p className="text-xl sm:text-2xl text-primary-100 max-w-3xl mx-auto leading-relaxed">
              Professional, ready-to-use website templates for businesses of all types. Get online quickly without sacrificing quality.
            </p>
          </div>
        </div>
      </section>

      {/* Featured Template - Family Physician */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="px-4 py-1 bg-primary-100 text-primary-800 text-sm font-medium rounded-full">
              Featured Template
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-4 mb-4">
              Family Physician Office
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A comprehensive website template designed specifically for family medicine practices.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img
                src="/templates/family-physician-preview.jpg"
                alt="Family Physician Template"
                className="rounded-lg shadow-xl"
              />
              <div className="absolute top-4 right-4">
                <span className="px-3 py-1 bg-accent-500 text-white text-sm font-medium rounded-full">
                  Popular
                </span>
              </div>
            </div>
            
            <div>
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Designed for Healthcare Professionals
              </h3>
              <p className="text-gray-600 mb-6 leading-relaxed">
                This template includes everything a family physician practice needs to establish a professional online presence, 
                attract new patients, and provide valuable resources to existing ones.
              </p>
              
              <div className="mb-6">
                <h4 className="font-semibold text-gray-900 mb-3">Key Features:</h4>
                <ul className="space-y-2">
                  <li className="flex items-center text-gray-600">
                    <CheckCircle className="h-5 w-5 text-accent-500 mr-3 flex-shrink-0" />
                    Online appointment booking system
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircle className="h-5 w-5 text-accent-500 mr-3 flex-shrink-0" />
                    Secure patient portal integration
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircle className="h-5 w-5 text-accent-500 mr-3 flex-shrink-0" />
                    Staff and physician profiles
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircle className="h-5 w-5 text-accent-500 mr-3 flex-shrink-0" />
                    Services and specialties section
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircle className="h-5 w-5 text-accent-500 mr-3 flex-shrink-0" />
                    Patient resources and health library
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircle className="h-5 w-5 text-accent-500 mr-3 flex-shrink-0" />
                    Insurance information page
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircle className="h-5 w-5 text-accent-500 mr-3 flex-shrink-0" />
                    Contact forms and location maps
                  </li>
                </ul>
              </div>
              
              <div className="flex items-center space-x-2 mb-6">
                <span className="text-sm text-gray-600">Primary Colors:</span>
                <div className="w-8 h-8 rounded-md border border-gray-200" style={{ backgroundColor: '#2563eb' }}></div>
                <div className="w-8 h-8 rounded-md border border-gray-200" style={{ backgroundColor: '#10b981' }}></div>
                <div className="w-8 h-8 rounded-md border border-gray-200" style={{ backgroundColor: '#ffffff' }}></div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/templates/family-physician" className="btn-primary text-center">
                  View Full Demo
                </Link>
                <Link to="/get-started" className="btn-outline text-center">
                  Get Started
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Templates Grid */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Browse All Templates
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find the perfect starting point for your website. All templates include responsive design and essential features.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {templates.map((template, index) => (
              <div key={index} className="card overflow-hidden group">
                <div className="relative overflow-hidden">
                  <img
                    src={template.image}
                    alt={template.title}
                    className="w-full h-48 object-cover transform group-hover:scale-105 transition-transform duration-500"
                  />
                  {template.popular && (
                    <div className="absolute top-4 right-4">
                      <span className="px-3 py-1 bg-accent-500 text-white text-sm font-medium rounded-full">
                        Popular
                      </span>
                    </div>
                  )}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-primary-600 text-white text-sm font-medium rounded-full">
                      {template.category}
                    </span>
                  </div>
                </div>
                
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{template.title}</h3>
                  <p className="text-gray-600 mb-4 leading-relaxed">{template.description}</p>
                  
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">Key Features:</h4>
                    <ul className="space-y-1">
                      {template.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center text-sm text-gray-600">
                          <CheckCircle className="h-4 w-4 text-accent-500 mr-2 flex-shrink-0" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <div className="flex items-center space-x-2 mb-4">
                    <span className="text-sm text-gray-600">Colors:</span>
                    {template.colors.map((color, colorIndex) => (
                      <div 
                        key={colorIndex} 
                        className="w-8 h-8 rounded-md border border-gray-200" 
                        style={{ backgroundColor: color }}
                        title={color}
                      ></div>
                    ))}
                  </div>
                  
                  <div className="flex items-center justify-end mt-6">
                    <Link 
                      to={template.demoLink}
                      className="inline-flex items-center text-primary-600 font-semibold hover:text-primary-700 transition-colors"
                    >
                      View Demo
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Getting your website up and running is simple with our streamlined process.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                1
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Choose a Template</h3>
              <p className="text-gray-600 leading-relaxed">Select the template that best fits your business needs and aesthetic preferences.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                2
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Customize</h3>
              <p className="text-gray-600 leading-relaxed">Provide your content, branding elements, and customization preferences.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                3
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Review</h3>
              <p className="text-gray-600 leading-relaxed">Review the customized template and request any adjustments needed.</p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 bg-primary-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6">
                4
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Launch</h3>
              <p className="text-gray-600 leading-relaxed">We'll handle the technical setup and launch your new website.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Common questions about our website templates.
            </p>
          </div>

          <div className="space-y-8">
            {faqs.map((faq, index) => (
              <div key={index} className="card p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">
                  {faq.question}
                </h3>
                <p className="text-gray-600">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary-600 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-primary-100 mb-8">
            Choose a template and get your professional website up and running in days, not weeks.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/get-started" className="btn-secondary text-lg px-8 py-4">
              Get Free Consultation
            </Link>
            <Link to="/contact" className="btn-outline text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-primary-600">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};